import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@viastud/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@viastud/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@viastud/ui/form'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { Input } from '@viastud/ui/input'
import { trpc } from '@viastud/ui/lib/trpc'
import { ConfirmLeaveModal } from '@viastud/ui/shared/confirm-leave-modal'
import type { BaseFormModalProps } from '@viastud/ui/types/base-form-modal'
import { type ImportChildSchema, importChildSchema } from '@viastud/utils'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

interface ImportChildModalProps extends BaseFormModalProps {
  isDirty: boolean
}

export const ImportChildModal = ({ refresh, isDirty }: ImportChildModalProps) => {
  const { toast } = useToast()
  const importChildForm = useForm<ImportChildSchema>({
    resolver: zodResolver(importChildSchema),
    defaultValues: {
      email: '',
    },
  })

  const [open, setOpen] = useState<boolean>(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false)

  const { mutateAsync: importChildMutation, isPending } = trpc.user.importChild.useMutation({
    onSuccess: () => {
      setOpen(false)
      refresh()
      importChildForm.reset()
      toast({
        title: 'Invitation envoyée',
        description: "Une invitation a été envoyée par email à l'élève.",
      })
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message || "Une erreur est survenue lors de l'envoi de l'invitation.",
        variant: 'destructive',
      })
    },
  })

  const onSubmit = async (data: ImportChildSchema) => {
    try {
      await importChildMutation(data)
    } catch {
      // Géré dans onError
    }
  }

  const handleClick = () => {
    if (isDirty) {
      setConfirmModalOpen(true)
    } else {
      setOpen(true)
    }
  }

  const handleClose = () => {
    setOpen(false)
    importChildForm.reset()
  }

  return (
    <>
      <ConfirmLeaveModal
        open={confirmModalOpen}
        title="Vous avez des modifications non sauvegardées pour vos enfants. Ceci annulera les changements. Êtes-vous sûr de vouloir continuer ?"
        cancel={() => {
          setConfirmModalOpen(false)
        }}
        confirm={() => {
          setConfirmModalOpen(false)
          setOpen(true)
        }}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <Button
          variant="outline"
          className="min-h-9 min-w-9 rounded-full border-blue-600 text-blue-600 hover:text-blue-700"
          onClick={handleClick}
        >
          <p className="font-semibold">Importer un enfant</p>
        </Button>
        <DialogContent>
          <Form {...importChildForm}>
            <form onSubmit={importChildForm.handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-950">
                  Importer un enfant
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-start gap-4 self-stretch py-4">
                <p className="text-sm text-gray-600">
                  Saisissez l'adresse email de votre enfant pour lui envoyer une demande de
                  rattachement. Il devra valider la demande depuis son espace personnel.
                </p>
                <FormField
                  control={importChildForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Adresse e-mail de l'enfant
                      </FormLabel>
                      <FormControl>
                        <Input className="w-full" placeholder="exemple@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="gap-4">
                <DialogClose asChild className="flex grow">
                  <Button
                    variant="outline"
                    className="text-sm font-semibold text-blue-800"
                    onClick={handleClose}
                  >
                    Annuler
                  </Button>
                </DialogClose>
                <Button
                  variant="default"
                  className="flex grow text-sm font-semibold"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? 'Envoi en cours...' : "Envoyer l'invitation"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}
